import {aiImageApi} from '../api/aiImageApi';
import {
    AiImageGroup,
    AiImageGroupDetail,
    AiImageRequest,
    AiImageRequestStatus,
    BaseImageUploadResponse,
    CreateAiImageGroupRequest,
    CreateAiImageRequestBody,
    UpdateAiImageGroupRequest,
} from '../types/aiImage';
import {
    useBaseMutation,
    useBaseQuery,
    useInvalidateQueries,
} from './useBaseQuery';

// ── Query Key Factory ────────────────────────────────────────────────────────

export const aiImageKeys = {
    all: ['aiImages'] as const,
    groups: () => [...aiImageKeys.all, 'groups'] as const,
    groupDetail: (id: number) =>
        [...aiImageKeys.all, 'groupDetail', id] as const,
    requests: (groupId: number) =>
        [...aiImageKeys.all, 'requests', groupId] as const,
    request: (id: number) => [...aiImageKeys.all, 'request', id] as const,
};

const ACTIVE_STATUSES: ReadonlySet<AiImageRequestStatus> = new Set([
    'PENDING',
    'RUNNING',
]);

const hasActiveRequest = (
    requests: AiImageRequest[] | undefined
): boolean => {
    if (!requests) return false;
    return requests.some((r) => ACTIVE_STATUSES.has(r.status));
};

// ── Queries ──────────────────────────────────────────────────────────────────

export const useAiImageGroups = () => {
    return useBaseQuery<AiImageGroup[]>(aiImageKeys.groups(), () =>
        aiImageApi.listGroups()
    );
};

export const useAiImageGroupDetail = (groupId: number | null | undefined) => {
    return useBaseQuery<AiImageGroupDetail>(
        aiImageKeys.groupDetail(groupId ?? 0),
        () => aiImageApi.getGroup(groupId as number),
        {
            enabled: !!groupId,
            // 그룹 detail에 포함된 최신 요청 중 진행 중인 게 있으면 5초 폴링
            refetchInterval: (query) => {
                const data = query.state.data as AiImageGroupDetail | undefined;
                return hasActiveRequest(data?.requests) ? 5000 : false;
            },
        }
    );
};

export const useAiImageRequests = (groupId: number | null | undefined) => {
    return useBaseQuery<AiImageRequest[]>(
        aiImageKeys.requests(groupId ?? 0),
        () => aiImageApi.listRequests(groupId as number),
        {
            enabled: !!groupId,
            refetchInterval: (query) => {
                const data = query.state.data as AiImageRequest[] | undefined;
                return hasActiveRequest(data) ? 5000 : false;
            },
        }
    );
};

export const useAiImageRequest = (requestId: number | null | undefined) => {
    return useBaseQuery<AiImageRequest>(
        aiImageKeys.request(requestId ?? 0),
        () => aiImageApi.getRequest(requestId as number),
        {
            enabled: !!requestId,
            // 단건 조회: status가 진행 중일 때 5초 폴링
            refetchInterval: (query) => {
                const data = query.state.data as AiImageRequest | undefined;
                return data && ACTIVE_STATUSES.has(data.status) ? 5000 : false;
            },
        }
    );
};

// ── Mutations ────────────────────────────────────────────────────────────────

export const useCreateAiImageGroup = () => {
    const {invalidate} = useInvalidateQueries();
    return useBaseMutation<CreateAiImageGroupRequest, AiImageGroup>(
        (body) => aiImageApi.createGroup(body),
        {
            onSuccess: () => {
                invalidate(aiImageKeys.groups());
            },
        }
    );
};

export const usePatchAiImageGroup = (groupId: number) => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<UpdateAiImageGroupRequest, AiImageGroup>(
        (body) => aiImageApi.updateGroup(groupId, body),
        {
            onSuccess: () => {
                invalidateMultiple([
                    aiImageKeys.groups(),
                    aiImageKeys.groupDetail(groupId),
                ]);
            },
        }
    );
};

export const useDeleteAiImageGroup = () => {
    const {invalidate} = useInvalidateQueries();
    return useBaseMutation<number, void>(
        (groupId) => aiImageApi.deleteGroup(groupId),
        {
            onSuccess: () => {
                invalidate(aiImageKeys.groups());
            },
        }
    );
};

export const useCreateAiImageRequest = (groupId: number) => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<CreateAiImageRequestBody, AiImageRequest>(
        (body) => aiImageApi.createRequest(groupId, body),
        {
            onSuccess: () => {
                invalidateMultiple([
                    aiImageKeys.requests(groupId),
                    aiImageKeys.groupDetail(groupId),
                ]);
            },
        }
    );
};

export const useCancelAiImageRequest = (groupId: number) => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<number, void>(
        (requestId) => aiImageApi.cancelRequest(requestId),
        {
            onSuccess: (_data, requestId) => {
                invalidateMultiple([
                    aiImageKeys.request(requestId),
                    aiImageKeys.requests(groupId),
                    aiImageKeys.groupDetail(groupId),
                ]);
            },
        }
    );
};

export interface UploadBaseImageVars {
    groupId: number;
    file: File;
}

export const useUploadAiImageBaseImage = () => {
    return useBaseMutation<UploadBaseImageVars, BaseImageUploadResponse>(
        ({groupId, file}) => aiImageApi.uploadBaseImage(groupId, file)
    );
};
